import React, { useState } from "react";
import {
  Box, Button, Chip, Grid, Paper, Typography, IconButton, Tooltip,
} from "@mui/material";
import {
  Add, AccountBalance, CheckCircle, Cancel, Receipt, Store, Description, Money, LocalAtm, Groups,
} from "@mui/icons-material";
import {
  updateCorpusDisbursement,
} from "../../../Config/db/corpus-disbursement.operations";
import CorpusDisbursementForm from "./CorpusDisbursementForm";

const STATUS_CHIP = {
  issued:    { label: "Issued", color: "warning" },
  cleared:   { label: "Cleared", color: "success" },
  cancelled: { label: "Cancelled", color: "error" },
};

const PAYMENT_MODE_DISPLAY = {
  cheque:          { label: "Cheque",     icon: AccountBalance },
  "cash-withdrawn": { label: "Cash (Bank)",  icon: LocalAtm },
  "cash-resident":  { label: "Cash (Residents)", icon: Groups },
  // Legacy fallbacks
  bank:            { label: "Cheque",     icon: AccountBalance },
  cash:            { label: "Cash",       icon: Money },
};

export default function CorpusDisbursements({ disbursements = [] }) {
  const [open, setOpen] = useState(false);

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      await updateCorpusDisbursement(id, { status: newStatus });
    } catch (err) {
      console.error("Failed to update disbursement:", err);
    }
  };

  const totalDebited = disbursements
    .filter((d) => d.status !== "cancelled")
    .reduce((s, d) => s + Number(d.amount || 0), 0);

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Corpus Disbursements
          </Typography>
          {totalDebited > 0 && (
            <Chip
              label={`Total: ₹${totalDebited.toLocaleString()}`}
              size="small"
              color="secondary"
              sx={{ fontWeight: 600 }}
            />
          )}
        </Box>
        <Button
          onClick={() => setOpen(true)}
          variant="contained"
          size="small"
          startIcon={<Add />}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            background: "linear-gradient(135deg, #6a1b9a 0%, #7b1fa2 100%)",
          }}
        >
          Record Debit
        </Button>
      </Box>

      <CorpusDisbursementForm
        open={open}
        handleClose={() => setOpen(false)}
        existingDisbursements={disbursements}
      />

      {disbursements.length === 0 && (
        <Paper
          elevation={0}
          sx={{ p: 4, textAlign: "center", border: "1px solid #e0e0e0", borderRadius: 2 }}
        >
          <AccountBalance sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
          <Typography color="text.secondary">
            No disbursements recorded yet. Use "Record Debit" to capture cheque
            issues or fund transfers from the corpus.
          </Typography>
        </Paper>
      )}

      <Grid container spacing={1.5}>
        {disbursements.map((d) => {
          const statusMeta = STATUS_CHIP[d.status] || STATUS_CHIP.issued;
          const ts = d.timestamp?.toDate ? d.timestamp.toDate() : new Date(d.timestamp?.seconds * 1000);
          return (
            <Grid item xs={12} key={d.id}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid #e0e0e0",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Receipt sx={{ color: "#6a1b9a", fontSize: 28 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      ₹{Number(d.amount).toLocaleString()}
                    </Typography>
                    <Chip
                      label={statusMeta.label}
                      size="small"
                      color={statusMeta.color}
                      sx={{ fontWeight: 600, fontSize: "0.7rem" }}
                    />
                    {d.chequeNumber && (
                      <Chip
                        label={`Ref: ${d.chequeNumber}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: "0.7rem" }}
                      />
                    )}
                    {(() => {
                      const mode = PAYMENT_MODE_DISPLAY[d.paymentMode] || PAYMENT_MODE_DISPLAY.cheque;
                      const ModeIcon = mode.icon;
                      return (
                        <Chip
                          icon={<ModeIcon sx={{ fontSize: 14 }} />}
                          label={mode.label}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: "0.7rem", fontWeight: 500 }}
                        />
                      );
                    })()}
                  </Box>
                  {/* Vendor & Contract info */}
                  {d.vendorName && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                      <Store sx={{ fontSize: 14, color: "#00897b" }} />
                      <Typography variant="body2" fontWeight={500} sx={{ color: "#00897b" }}>
                        {d.vendorName}
                      </Typography>
                      {d.contractTitle && (
                        <>
                          <Typography variant="body2" color="text.secondary" sx={{ mx: 0.25 }}>→</Typography>
                          <Description sx={{ fontSize: 13, color: "#757575" }} />
                          <Typography variant="body2" color="text.secondary">
                            {d.contractTitle}
                          </Typography>
                        </>
                      )}
                    </Box>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {[d.bankName, !d.vendorName && d.payee].filter(Boolean).join(" • ")}
                    {(d.bankName || (!d.vendorName && d.payee)) ? " • " : ""}
                    {ts.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </Typography>
                  {d.remarks && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: "0.8rem" }}>
                      {d.remarks}
                    </Typography>
                  )}
                </Box>

                {/* Status actions */}
                {d.status === "issued" && (
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Tooltip title="Mark Cleared">
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleStatusUpdate(d.id, "cleared")}
                      >
                        <CheckCircle fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleStatusUpdate(d.id, "cancelled")}
                      >
                        <Cancel fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
